/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { withBasePath } from "@cosmos/common-utils";

import { jsonLdSafeStringify } from "../metadata/structured-data";

/*
  tracking.js stamps `window._epicAccountId` onto every event, ahead of its
  server-side lookup from the legacy EPIC_SSO / EPIC_BEARER_TOKEN cookies. Those
  cookies stopped being refreshed across Epic hosts after the WebSSO cutover
  (2026-04-22), so without it the account_id fill rate decays.

  It has to be in the server HTML rather than set from a client effect: tracking.js
  sends its first page view once the document is parsed (DOMContentLoaded), which
  can be before hydration, and every parser-inserted script has run by then.
  Signed-out HTML carries no account-shaped field at all. `TrackingAccountIdSync`
  (`@cosmos/next-hooks`) keeps the value current after load.
*/
const accountIdScript = (accountId: string): React.JSX.Element => (
	<script
		key="tracking-account-id"
		dangerouslySetInnerHTML={{ __html: `window._epicAccountId=${jsonLdSafeStringify(accountId)};` }}
	/>
);

interface TrackingProps {
	locale?: string;
	/** Override for NEXT_PUBLIC_TRACKING_BASE */
	trackingBase?: string;
	/** Override for NEXT_PUBLIC_TRACKING_DISABLED */
	trackingDisabled?: string;
	/** Override for NEXT_PUBLIC_DSA_TRACKING_ID */
	dsaTrackingId?: string;
	/** Override for NEXT_PUBLIC_TRACKING_VERSION */
	trackingVersion?: string;
	/** Override for NEXT_PUBLIC_TRACKING_SCRIPT_HASH */
	trackingScriptHash?: string;
	/**
	 * Site base path (e.g. `/o`) prepended to a root-relative tracking base, for sites whose
	 * edge route covers only that prefix. Ignored for an absolute base.
	 */
	basePath?: string;
	/**
	 * The page's Epic account. When signed in, its ID is exposed to tracking.js as
	 * `window._epicAccountId`; pair with `TrackingAccountIdSync` inside `AccountProvider`.
	 */
	account?: { isLoggedIn?: boolean; id?: string };
}

export const Tracking = ({
	locale,
	trackingBase,
	trackingDisabled,
	dsaTrackingId,
	trackingVersion,
	trackingScriptHash,
	basePath,
	account,
}: TrackingProps): React.JSX.Element | null => {
	const resolvedDisabled = trackingDisabled ?? process.env.NEXT_PUBLIC_TRACKING_DISABLED;
	const resolvedBase = trackingBase ?? process.env.NEXT_PUBLIC_TRACKING_BASE;
	const resolvedDsaId = dsaTrackingId ?? process.env.NEXT_PUBLIC_DSA_TRACKING_ID;
	const resolvedVersion = trackingVersion ?? process.env.NEXT_PUBLIC_TRACKING_VERSION;
	const resolvedHash = trackingScriptHash ?? process.env.NEXT_PUBLIC_TRACKING_SCRIPT_HASH;

	const trackingEnabled = Boolean((!resolvedDisabled || resolvedDisabled === "false") && resolvedBase);

	if (trackingEnabled) {
		// `resolvedBase` may be absolute (e.g. https://tracking.epicgames.com) or
		// root-relative (e.g. /tracking-api when the script is served first-party
		// via the tracking-site target group). Build the src by string so both
		// forms work; only `URLSearchParams` is used, for query encoding.
		const params = new URLSearchParams();

		if (resolvedDsaId) {
			params.set("p", resolvedDsaId);
		}

		if (locale) {
			params.set("l", locale);
		}

		const withVersion = Boolean(resolvedVersion && resolvedHash);
		if (withVersion) {
			params.set("version", resolvedVersion as string);
		}

		const trimmedBase = (resolvedBase ?? "").replace(/\/+$/, "");
		const base = trimmedBase.startsWith("/") ? withBasePath(trimmedBase, basePath ?? "") : trimmedBase;
		const query = params.toString();
		const src = `${base}/tracking.js${query ? `?${query}` : ""}`;

		const trackingScript = withVersion ? (
			<script key="tracking-script" crossOrigin="anonymous" integrity={resolvedHash} src={src} async />
		) : (
			<script key="tracking-script" src={src} async />
		);

		const accountId = account?.isLoggedIn ? account.id : undefined;
		if (!accountId) {
			return trackingScript;
		}

		return (
			<>
				{accountIdScript(accountId)}
				{trackingScript}
			</>
		);
	}

	return null;
};
